import axios, {AxiosResponse} from "axios";
import Cookies from "js-cookie";
import GutenbergRequest from "../interfaces/GutenbergRequest";
import MachiavelliRequest from "../interfaces/MachiavelliRequest";

/**
 * Central API service for Davinci.
 *
 * Davinci is a browser-only SPA: it owns no database and reaches every backend
 * over HTTP. This module exposes one thin axios client per upstream service in
 * the Renaissance ecosystem, each pre-wired with the bearer token issued by
 * the authorization service:
 *
 * - {@link MachiavelliClient} — the Machiavelli dashboard API (Laravel).
 * - {@link GutenbergClient} — the Gutenberg test-order API.
 * - {@link ShAuthorizationClient} — the `sh-auth` authorization service itself.
 *
 * The session token lives in `localStorage` under `shToken` /
 * `shTokenExpiration`; {@link ShAuthorizationRefresh} keeps it fresh ahead of
 * expiry. See `docs/integrations.md` and `docs/state-management.md`.
 *
 * @module app/services/api
 */

/**
 * Silently refresh the `sh-auth` access token when it is within one hour of
 * expiry.
 *
 * Reads `shTokenExpiration` / `shClientId` / `shToken` from `localStorage`,
 * POSTs to `api/v1/refresh` on the authorization service, and writes the new
 * `access_token` / `expires_at` back to `localStorage`. A no-op when the
 * current token is still comfortably valid. Errors are swallowed (logged to
 * the console) so a transient refresh failure does not break the pending call.
 *
 * @returns Resolves once the refresh attempt has completed (or was skipped).
 */
async function ShAuthorizationRefresh(): Promise<void> {

    const tokenExpiration = localStorage.getItem('shTokenExpiration') ?? '';

    const isOneHourBeforeExpiration = () => {
        const expirationDate = new Date(tokenExpiration);
        const currentTime = new Date();

        const timeDifference = expirationDate.getTime() - currentTime.getTime();
        return timeDifference <= 3600000; //refresh 1 hour before expiry
    };

    if (!isOneHourBeforeExpiration()) {
        return;
    }

    const clientId = localStorage.getItem('shClientId') ?? '';

    try {
        const response = await axios.request({
            method: 'POST',
            url: 'api/v1/refresh',
            baseURL: process.env.REACT_APP_AUTHORIZATION_API_URL,
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': Cookies.get('XSRF-TOKEN'),
                Authorization: 'Bearer ' + (localStorage.getItem('shToken') ?? ''),
            },
            data: { client_id: clientId },
        });

        localStorage.setItem('shToken', response.data.access_token);
        localStorage.setItem('shTokenExpiration', response.data.expires_at);

        return;
    } catch (error) {
        console.error(error);
        return;
    }

}

/**
 * Authenticated client for the `sh-auth` authorization service.
 *
 * Refreshes the token, then issues the request described by `props` against
 * `REACT_APP_AUTHORIZATION_API_URL` with the bearer token and the CSRF cookie
 * (`XSRF-TOKEN`) attached. Used for account/role/session calls that target the
 * authorization service directly.
 *
 * @param props  Request descriptor: `uri`, `method` (default `get`), optional `data`.
 * @returns The axios response for the call.
 */
const ShAuthorizationClient: (props: MachiavelliRequest) => Promise<AxiosResponse> = async (props: MachiavelliRequest) => {

    await ShAuthorizationRefresh();

    const uri = props.uri ?? "";
    const method = props.method ?? "get";
    const token = localStorage.getItem('shToken') ?? '';
    const data = props.data ?? undefined;

    const apiClient = axios.create({
        baseURL: process.env.REACT_APP_AUTHORIZATION_API_URL,
        withCredentials: true,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': Cookies.get('XSRF-TOKEN'),
        }
    });

    const authHeader = () => {
        return {Authorization: 'Bearer ' + token};
    }

    return apiClient.request({
            method: method,
            url: uri,
            data: data,
            headers: authHeader(),
        }
    );

}
/**
 * Authenticated client for the Machiavelli dashboard API (Laravel).
 *
 * Refreshes the token, then dispatches to the matching axios verb based on
 * `props.method` (`POST` / `PATCH` / `DELETE`, falling back to `GET`) against
 * `REACT_APP_MACHIAVELLI_API_URL`. This is the workhorse client behind most
 * Davinci feature modules (Akeneo, Colorlab, SKU finder, virtual products).
 *
 * @param props  Request descriptor: `uri`, `method` (default `get`), optional `data`.
 * @returns The axios response for the call.
 */
const MachiavelliClient: (props: MachiavelliRequest) => Promise<AxiosResponse> = async (props: MachiavelliRequest) => {

    await ShAuthorizationRefresh();

    const uri = props.uri ?? "";
    const method = props.method ?? "get";
    const token = localStorage.getItem('shToken') ?? '';
    const tokenExpiration = localStorage.getItem('shTokenExpiration') ?? '';
    const data = props.data ?? undefined;

    const apiClient = axios.create({
        baseURL: process.env.REACT_APP_MACHIAVELLI_API_URL,
        withCredentials: true,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }
    });

    const authHeader = () => {
        return {Authorization: 'Bearer ' + token};
    }

    if (method.toUpperCase() === "POST") {
        return apiClient.post(uri, data, {
                headers: authHeader()
            }
        )
    }
    if (method.toUpperCase() === "PATCH") {
        return apiClient.patch(uri, data, {
                headers: authHeader()
            }
        )
    }
    if (method.toUpperCase() === "DELETE") {
        return apiClient.delete(uri, {
                headers: authHeader()
            }
        )
    }

    return apiClient.get(uri, {
        headers: authHeader()
    });

}

/**
 * Authenticated client for the Gutenberg test-order API.
 *
 * Same shape as {@link MachiavelliClient} but targets
 * `REACT_APP_GUTENBERG_API_URL`. Used by the Gutenberg module to create and
 * process test orders. Gutenberg is optional locally — only required when
 * working on the testing endpoints.
 *
 * @param props  Request descriptor: `uri`, `method` (default `get`), optional `data`.
 * @returns The axios response for the call.
 */
const GutenbergClient: (props: GutenbergRequest) => Promise<AxiosResponse> = async (props: GutenbergRequest) => {

    await ShAuthorizationRefresh();


    const uri = props.uri ?? "";
    const method = props.method ?? "get";
    const token = localStorage.getItem('shToken') ?? '';
    const tokenExpiration = localStorage.getItem('shTokenExpiration') ?? '';
    const data = props.data ?? undefined;

    const apiClient = axios.create({
        baseURL: process.env.REACT_APP_GUTENBERG_API_URL,
        withCredentials: true,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        }
    });

    const authHeader = () => {
        return {Authorization: 'Bearer ' + token};
    }

    // const csrf = async () => {
    //     return apiClient.get('XSRF-TOKEN')
    // }
    //
    // return csrf().then(() => {

    if (method.toUpperCase() === "POST") {
        return apiClient.post(uri, data, {
                headers: authHeader()
            }
        )
    }
    if (method.toUpperCase() === "PATCH") {
        return apiClient.patch(uri, data, {
                headers: authHeader()
            }
        )
    }
    if (method.toUpperCase() === "DELETE") {
        return apiClient.delete(uri, {
                headers: authHeader()
            }
        )
    }

    return apiClient.get(uri, {
        headers: authHeader()
    });
    // });
}

export default {MachiavelliClient, GutenbergClient, ShAuthorizationClient};
